import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import sortBy from 'lodash/sortBy'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { trackingEvents } from 'modules/core/lib/Tracking'
import { track } from 'modules/core/lib/Tracking/tracker'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { positionOptions } from 'modules/processes/components/ProcessSettings/Steps/StepUpsertModal'
import { ProcessDetail } from 'modules/processes/domain/process'
import { useDuplicateStep } from 'modules/processes/hooks/useDuplicateStep'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'
import { useValidateStringLength } from 'modules/tasks/hooks/useValidateStringLength'

interface StepFormValues {
  nameError: string | undefined
  name: string
  duplicateStepId: number | undefined
  offsetDirection: string
  stepToSwapId: number | undefined | null
}

type Props = {
  process: ProcessDetail
  stepToDuplicateId: number
  onSuccess: () => void
  onClose: () => void
}

const DuplicateProcessModal = observer(
  ({ process, onSuccess, onClose, stepToDuplicateId }: Props) => {
    const [submitting, setSubmitting] = useState<boolean>(false)
    const { duplicateStep } = useDuplicateStep()
    const stepToDuplicate = process?.steps.find((step) => step.id === stepToDuplicateId)
    const sortedSteps = sortBy(process?.steps || [], (step) => Number(step.position)) || []
    const firstStep = sortedSteps[0]
    const { isValid: isStepNameValid } = useValidateStringLength()
    const { values, setValues, setErrors, handleSubmit } = useForm<StepFormValues>({
      defaultValues: {
        nameError: undefined,
        name: stepToDuplicate?.name || '',
        duplicateStepId: stepToDuplicateId,
        offsetDirection: 'after',
        stepToSwapId: firstStep?.id,
      },
      onSubmit,
    })

    async function onSubmit() {
      track(trackingEvents.processes.duplicateStepCreated, {
        category: process.category,
        processId: process.id,
        stepId: stepToDuplicateId,
      })

      setSubmitting(true)
      const handled = await handleDuplicateStep()

      if (handled.success && handled.data.step) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }
      onSuccess()
      setSubmitting(false)
      return handled
    }

    const handleDuplicateStep = async () => {
      const result = await duplicateStep(
        {
          name: values.name,
          duplicateStepId: stepToDuplicateId,
          stepToSwapId: Number(values.stepToSwapId),
          offsetDirection: values.offsetDirection,
        },
        Number(process?.id)
      )

      return handleResult(result, (errors) => {
        if (errors) setErrors(errors)
      })
    }

    function onChange(type: keyof StepFormValues) {
      return function (value: string) {
        setValues({ ...values, [type]: value })
      }
    }

    const progressionCondition = stepToDuplicate?.progressionCondition
    const showStepSelection = progressionCondition && process?.steps && process?.steps?.length >= 1

    const stepsOption: SelectOption[] =
      sortedSteps.map((step) => ({
        value: step.id,
        label: step.name,
      })) || []

    return (
      <Modal
        illustration="add-block"
        title={i18n.t('processes.process_settings.steps.duplicate')}
        onClose={onClose}
        footer={
          <Button disabled={submitting} onClick={() => handleSubmit()}>
            {i18n.t('common.submit')}
          </Button>
        }
      >
        <FormRow>
          <Box gap="s8" paddingY="s12">
            <Text.Normal size="300" weight="semibold" capitalize>
              {i18n.t('processes.process_settings.steps.modal.step_name')}
            </Text.Normal>
          </Box>
          <TextField
            mandatory
            type="text"
            value={values.name}
            error={values.nameError}
            isNotValid={values.nameError ? !isStepNameValid : false}
            onChange={(value: string) =>
              setValues({ ...values, name: value, nameError: values.nameError })
            }
          />
        </FormRow>
        {showStepSelection && (
          <Box>
            <Box paddingBottom="s4">
              <Text.Normal weight="medium" size="200" color="grey1000">
                {i18n.t('processes.process_settings.steps.modal.position')}
              </Text.Normal>
            </Box>
            <Box gap="s12" flexDirection="row">
              <Select
                value={values.offsetDirection ? String(values.offsetDirection) : undefined}
                onChange={onChange('offsetDirection')}
                options={positionOptions()}
              />
              <Select
                value={values.stepToSwapId ? String(values.stepToSwapId) : undefined}
                onChange={onChange('stepToSwapId')}
                options={stepsOption}
              />
            </Box>
          </Box>
        )}
      </Modal>
    )
  }
)

const DuplicateStepModalPage = () => {
  const navigate = useNavigate()
  const { stepToDuplicateId } = useParams()
  const { process } = useProcessContext()
  const { navigation } = useNavigation()

  const onSuccess = () => {
    navigate(
      navigation.processes.template.id.settings.fullPath({
        category: String(process.category),
        id: process.id,
      }),
      {
        replace: true,
      }
    )
  }

  const onClose = () => {
    navigate(-1)
  }

  if (!process) return null

  return (
    <DuplicateProcessModal
      process={process}
      onClose={onClose}
      onSuccess={onSuccess}
      stepToDuplicateId={Number(stepToDuplicateId)}
    />
  )
}

export default DuplicateStepModalPage
